import {
  parseIdParam,
  CodingActivityCard,
  DataStatusNote,
  deserify,
  formatClassSection,
  formatDurationBreakdown,
  formatScore,
  HeaderButton,
  LearningOutcomesCard,
  OverviewSection,
  performanceLabel,
  projectLabel,
  ReportLayout,
  StatTile,
  StudentProjectAnalysis,
  StudentProjectWorkTabs,
  TimeSpentCard,
  toPercent,
  uploadsToMedia,
  useCommonServiceClientContext,
  useGilReport,
} from '@geneo2-web/shared-ui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CodeIcon from '@mui/icons-material/Code';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { Student_Gil_Journey } from '../../../routeHandling/RoutesNomenclature';

/** Dashboard 1 - the student's own time, score and work on one project. */
export const StudentGilProjectReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const assessmentId = parseIdParam(
    new URLSearchParams(location.search).get('assessmentId')
  );
  const studentId = deserify(
    useAppSelector((state) => state.auth.userInfo?.studentProfileId)
  );
  const { LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const { state, reload } = useGilReport(
    () =>
      studentId && assessmentId
        ? LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getGilStudentProjectReport(
            { assessmentId }
          ).then((response) => response.data)
        : undefined,
    [studentId?.toString(), assessmentId]
  );

  const report = state.status === 'success' ? state.data : undefined;
  //~ On a group project the group's shared lab device did the work, so the
  //~ group's time and coding are shown as the student's own.
  const groupWork = report?.groupWork;
  const timeSpent = groupWork?.timeSpent ?? report?.timeSpent;
  const {
    parts: [videoText, codingText],
    total: totalText,
  } = formatDurationBreakdown([
    timeSpent?.videoActivityBookTimeInSec ?? 0,
    timeSpent?.codingTimeInSec ?? 0,
  ]);
  const score = report?.projectScore;
  const scorePercent = score?.isGraded
    ? toPercent(score.obtainedMarks, score.maxMarks)
    : undefined;
  const gallery = useMemo(
    () => uploadsToMedia(report?.userUploads),
    [report?.userUploads]
  );

  return (
    <ReportLayout
      showImage
      title={report ? projectLabel(report.project) : 'My Project Report'}
      subtitle={
        report ? `Class ${formatClassSection(report.classSection)}` : undefined
      }
      meta={
        report && [
          {
            label: 'Group',
            value: report.groupName,
            color: '#FFF8E6',
          },
        ]
      }
      headerRight={
        <HeaderButton
          label="My GIL Journey"
          icon={<TimelineIcon />}
          onClick={() => navigate(Student_Gil_Journey)}
        />
      }
      state={state}
      onRetry={reload}
    >
      <DataStatusNote status={report?.dataStatus} />

      <OverviewSection subtitle="Quick summary of activity and performance">
        <StatTile
          tone="yellow"
          icon={<AccessTimeIcon />}
          value={totalText}
          label={groupWork ? 'Time Spent' : 'Total Time Spent'}
        />
        <StatTile
          tone="purple"
          icon={<CodeIcon />}
          value={codingText}
          label="Coding Time"
        />
        <StatTile
          tone="green"
          icon="gil-group-green"
          value={videoText}
          label="Time Spent on Resources"
        />
        <StatTile
          tone="red"
          icon="gil-star-red"
          value={formatScore(score)}
          label="Project Score"
          caption={performanceLabel(scorePercent)}
        />
      </OverviewSection>

      <TimeSpentCard
        videoSec={timeSpent?.videoActivityBookTimeInSec}
        codingSec={timeSpent?.codingTimeInSec}
        subtitle={
          groupWork
            ? `Built with ${groupWork.groupName || 'your group'}`
            : 'Your learning time across all activities of this project'
        }
        effortText="You are putting consistent effort into your learning journey."
      />

      <CodingActivityCard
        activity={groupWork?.codingActivity ?? report?.codingActivity}
        title={groupWork ? 'Our Coding Work' : 'My Coding Work'}
        subtitle={
          groupWork
            ? `What ${groupWork.groupName || 'your group'} did in the coding editor`
            : 'What you did in the coding editor'
        }
      />

      <StudentProjectAnalysis report={report} />

      <LearningOutcomesCard
        outcomes={report?.learningOutcomes}
        showMarks
        subtitle="How you are doing on each learning outcome of this project"
      />

      <StudentProjectWorkTabs
        gallery={gallery}
        galleryTitle={report?.studentName}
        evidenceSubtitle="Photos and videos you uploaded"
      />
    </ReportLayout>
  );
};

export default StudentGilProjectReport;
